/**
 * The colours each shingle comes in, and which document shows which.
 *
 * A customer choosing a roof is choosing a colour at the same time, and the
 * lineup is a property of the shingle rather than of the package: Peace of Mind
 * is UHDZ and UHDZ comes in six colours, whoever is selling it and whatever it
 * is called this year. So the match is made on the **product**, not the package
 * name (Jonathon, 2026-08-16: "I want the color option for that particular
 * shingle option to show whenever that option is presented").
 *
 * Bare Bones has no lineup here on purpose. It is whatever three-tab is cheap
 * that week — "we're not going to put any color options for barebones since
 * they fluctuate so much" — and a colour chart that is wrong half the time is
 * worse than no colour chart.
 *
 * The metal panels are here too, for all that the file is named for shingles:
 * a package is a package, and the customer's question — "what colours does this
 * come in" — is the same one whatever the roof is made of.
 *
 * TimberSteel carries its five standard colours and its three specialty ones
 * in the order GAF puts them in, standard first: nobody is sold a red roof by
 * accident, and the three that could be are worth arriving after the five that
 * cannot.
 *
 * The photographs are the manufacturers' own, from the literature they publish
 * for exactly this — a contractor showing a homeowner what a roof looks like.
 * Every one of them is a real roof rather than a rendered swatch, which is the
 * only way anybody has ever chosen a colour.
 */

/** One colour, with the photographs of it. */
export interface ShingleColor {
  /** Stable, and the anchor a jump link points at. */
  key: string;
  name: string;
  /** Photograph keys: the house first, then the shingle close up. */
  photos: string[];
}

/** One shingle, with everything it comes in. */
export interface ShingleLine {
  key: string;
  /** The product, as the customer reads it: "GAF Timberline HDZ". */
  shingle: string;
  /**
   * What identifies this shingle on an estimate. Tried in list order, so a
   * narrower pattern has to come before a wider one that would also match —
   * see `SHINGLE_LINES` for the UHDZ/HDZ pair, which is the whole reason
   * order matters here.
   */
  matches: RegExp[];
  colors: ShingleColor[];
  /**
   * The manufacturer's own colour page, when there is one.
   *
   * A line that has one shows a card that opens it, the way the colour sheet
   * Jonathon worked up does; a line that does not falls back to the chart of
   * photographs held here. Both are a card and a "View colors" link on the
   * customer's copy — the difference is only where tapping it lands.
   */
  colorsUrl?: string;
  /**
   * A swipeable viewer of finished roofs, one colour per slide, when we hold
   * one. It opens over the proposal and takes the place of `colorsUrl`.
   */
  viewer?: ColorViewer;
}

/** One slide in a colour viewer. */
export interface ColorSlide {
  /** Path under the web app's `public/`. */
  src: string;
  /** The manufacturer's name for the colour, exactly as they print it. */
  name: string;
  /** Carries the "Premium Color" badge. */
  premium?: boolean;
  /** The full colour chart rather than a roof: shown whole, on white. */
  chart?: boolean;
  /**
   * The colour itself, as a hex. A viewer with a `grid` draws it as the chip
   * beside the name, as the fallback behind the photograph while it loads,
   * and as the swatch on the grid.
   */
  hex?: string;
}

/** A colour viewer: the heading over it, then its slides in order. */
export interface ColorViewer {
  eyebrow: string;
  title: string;
  slides: ColorSlide[];
  /**
   * A last slide of every colour as a swatch, instead of a chart image.
   *
   * Siding's viewer has one. Its photographs are close-ups of the texture,
   * so each fills the frame, and the slide after the last is a grid of
   * every colour that jumps to its photograph when tapped. The metal viewers
   * have none: their last slide is the chart, which is one of `slides`.
   */
  grid?: {
    heading: string;
    /** The manufacturer's own words, printed under the grid verbatim. */
    disclaimer: string;
  };
}

/** A roof in a viewer: the file, the colour, and whether it is premium. */
type Roof = readonly [file: string, name: string, premium?: boolean];

/**
 * A viewer's slides: its roofs in order, then the full chart last.
 *
 * The paths this builds are not only rendered: `buildPresentedDocument` copies
 * them into `proposals.snapshot`, and a presented document is frozen at send
 * (ADR 0032). Every proposal a customer holds points at these exact files
 * under `apps/web/public/metal-colors` for as long as its link lives. So a
 * file there is never renamed or removed and a folder is never restructured:
 * a photograph that changes gets a new file, and the old one stays for the
 * documents that pin it (ADR 0237, and the README beside the files). Every
 * path this has ever built is on `metal-colors-manifest.json` in
 * `apps/web/src/features/present`, and a test there checks the folder
 * against it.
 */
const viewerSlides = (
  folder: string,
  extension: string,
  roofs: readonly Roof[],
): ColorSlide[] => [
  ...roofs.map(([file, name, premium]): ColorSlide => ({
    src: `/metal-colors/${folder}/${file}.${extension}`,
    name,
    ...(premium ? { premium: true } : {}),
  })),
  {
    src: `/metal-colors/${folder}/chart.png`,
    name: "Full Color Chart",
    chart: true,
  },
];

/**
 * The Standing Seam viewer, from the design handoff Jonathon supplied on
 * 2026-09-28: twelve finished roofs, then the CopperRidge chart as the last
 * slide.
 *
 * The names are the manufacturer's. It is "Forest", not "Forest Green", and
 * "Copper Metallic", not "Copper". Matte Black and Copper Metallic are the two
 * the chart sells as premium finishes. Jonathon confirmed the same day that we
 * have the rights to the photographs.
 */
const STANDING_SEAM_VIEWER: ColorViewer = {
  eyebrow: "Snap-Lock Standing Seam · 26-Gauge",
  title: "Metal Roof Colors",
  slides: viewerSlides("standing-seam", "jpg", [
    ["01", "Black"],
    ["02", "Black"],
    ["03", "Charcoal"],
    ["04", "Burnished Slate"],
    ["05", "Burnished Slate"],
    ["06", "Brown"],
    ["07", "Forest"],
    ["08", "Matte Black", true],
    ["09", "Copper Metallic", true],
    ["10", "Copper Metallic", true],
    ["11", "Galvalume"],
    ["12", "Galvalume"],
  ]),
};

/**
 * The Ag Panel viewer, from the second handoff Jonathon supplied on
 * 2026-09-28: seven finished roofs, then the CopperRidge Ag Panel chart. It
 * replaces the PDF chart the card opened earlier the same day.
 *
 * The names are the manufacturer's again: "Rustic", not "Rustic Red", and
 * "Hunter", not "Hunter Green". None is premium. Copper Metallic is the Ag
 * Panel's upgrade colour but has no photograph yet, and the chart slide still
 * shows it.
 */
const AG_PANEL_VIEWER: ColorViewer = {
  eyebrow: "Ag Panel · Exposed Fastener",
  title: "Metal Roof Colors",
  slides: viewerSlides("ag-panel", "jpg", [
    ["charcoal", "Charcoal"],
    ["burnished-slate", "Burnished Slate"],
    ["brown", "Brown"],
    ["burgundy", "Burgundy"],
    ["rustic", "Rustic"],
    ["hunter", "Hunter"],
    ["forest", "Forest"],
  ]),
};

/**
 * What every one of these charts has to say.
 *
 * The manufacturers print it on their own colour pages and they are right: a
 * granule blend photographed, printed, converted and shown on a phone in
 * daylight is an approximation. Saying so is not a disclaimer to hide, it is
 * how somebody avoids choosing Charcoal and receiving what they thought was
 * Slate.
 */
export const SHINGLE_COLOR_NOTE =
  "Colors on a screen are never exact. Ask your rep for full-size samples to hold up against your house before you decide.";

const colors = (
  prefix: string,
  entries: readonly [name: string, photos: number][],
): ShingleColor[] =>
  entries.map(([name, photos]) => {
    const key = `${prefix}-${name.toLowerCase().replace(/\s+/g, "-")}`;
    return {
      key,
      name,
      photos: Array.from({ length: photos }, (_, i) => `${key}-${i + 1}`),
    };
  });

/**
 * The lineups, in the order they are matched.
 *
 * **UHDZ before HDZ.** "GAF Timberline UHDZ" contains the letters HDZ, so a
 * lineup matching `/hdz/` first would sell every UHDZ customer the twelve
 * colours of the cheaper shingle — six of which UHDZ is not made in.
 */
export const SHINGLE_LINES: readonly ShingleLine[] = [
  {
    key: "timbersteel",
    shingle: "GAF TimberSteel",
    // "Timber Steel Material" is what the catalogue calls the panel, and
    // "GAF TimberSteel" is what a rep types. Both are this roof.
    matches: [/timber\s*steel/i],
    colorsUrl:
      "https://www.gaf.com/en-us/document-library/documents/brochures-&-literature/timbersteel-system-brochure-rests203.pdf",
    colors: colors("timbersteel", [
      ["Obsidian Black", 2],
      ["Charcoal Gray", 2],
      ["Platinum Gray", 2],
      ["Dark Bronze", 2],
      ["Terra Bronze", 2],
      ["Rustic Red", 2],
      ["Mariner Blue", 2],
      ["Evergreen", 2],
    ]),
  },
  {
    key: "uhdz",
    shingle: "GAF Timberline UHDZ",
    matches: [/uhdz/i],
    colorsUrl:
      "https://drive.google.com/file/d/1_RS_pZ6XzggdWIiztomN65Obp_K1tBEC/view",
    colors: colors("uhdz", [
      ["Pewter Gray", 2],
      ["Charcoal", 2],
      ["Slate", 2],
      ["Barkwood", 2],
      ["Shakewood", 2],
      ["Weathered Wood", 2],
    ]),
  },
  {
    key: "hdz",
    shingle: "GAF Timberline HDZ",
    matches: [/hdz/i],
    colorsUrl:
      "https://drive.google.com/file/d/1nse9wN9U4EUxCnoiR65WfbKcsFKULF2Y/view",
    colors: colors("hdz", [
      ["Shakewood", 2],
      ["Charcoal", 2],
      ["Midnight Mesa", 2],
      ["Pewter Gray", 2],
      ["Cliffside", 2],
      ["Weathered Wood", 2],
      ["Sierra Sand", 2],
      ["Barkwood", 2],
      ["Chestnut Valley", 2],
      ["Mission Brown", 2],
      ["Hickory", 2],
      ["Slate", 2],
    ]),
  },
  /*
   * The metal panels. They hold no photographs of their own: each card opens
   * a published chart.
   *
   * All three got their own on 2026-09-28 (Jonathon: "ag panel will get this
   * new chart. i'm working on r panel and standing seam getting their own
   * chart"). The R Panel opens a PDF chart; the Ag Panel and Standing Seam
   * open a viewer of finished roofs that ends on their chart. All of it
   * is served from this app rather than a Drive link, so it lives in the repo
   * with the code that points at it.
   */
  {
    key: "standing-seam",
    shingle: "26 Gauge Standing Seam Snaplock",
    matches: [/standing\s*seam/i, /snap\s*lock/i],
    viewer: STANDING_SEAM_VIEWER,
    colors: [],
  },
  /*
   * R Panel before the Ag Panel entry, which also claims the generic
   * "Exposed Fastener Metal Panel" roof type. That generic type is what the
   * 29 gauge Ag Panel template carries, so it has to land on the Ag Panel
   * chart. The commercial R Panel template carries it too and lands there as
   * well until it is given a roof type of its own.
   */
  {
    key: "r-panel",
    shingle: "26 Gauge Exposed Fastener Metal Panels",
    matches: [/\br\s*panel/i],
    colorsUrl: "/metal-colors/r-panel-color-chart.pdf",
    colors: [],
  },
  {
    // The key predates the R Panel split and is kept so that an estimate
    // whose rep switched this card off keeps it off.
    key: "exposed-fastener",
    shingle: "26 Gauge Exposed Fastener Metal Panels",
    matches: [/exposed\s*fastener/i, /\bag\s*panel/i],
    viewer: AG_PANEL_VIEWER,
    colors: [],
  },
  {
    key: "titan-xt",
    // TAMKO's own spelling of the greys. It is what the catalogue carries and
    // what a supplier's paperwork will say, so it is what the customer reads.
    shingle: "TAMKO Titan XT",
    matches: [/titan\s*xt/i],
    colors: colors("titan-xt", [
      ["Shadow Grey", 1],
      ["Weathered Wood", 1],
      ["Rustic Slate", 1],
      ["Rustic Hickory", 1],
      ["Rustic Black", 1],
      ["Oxford Grey", 1],
      ["Thunderstorm Grey", 1],
      ["Natural Timber", 1],
      ["Black Walnut", 1],
    ]),
  },
];

/**
 * The lineup for a roof, from whatever names describe it.
 *
 * Fed the shingle type off the estimate and every line item the customer is
 * being shown, because either can be the thing that names the product: the
 * shingle type is typed on the estimate and the line items come off the
 * template.
 *
 * **The names are tried in the order they are given, and the first one that
 * names a shingle wins.** They can disagree — a TimberSteel estimate carrying
 * a leftover Timberline line, an estimate whose type says Titan XT while the
 * items still say HDZ — and when they do, the answer has to come from what the
 * caller trusts most rather than from where a lineup happens to sit in the
 * list below. Callers pass the shingle type first: it is what a rep typed on
 * the contract this customer is about to sign.
 *
 * Null when nothing matches — Bare Bones, a metal roof, a repair, or a shingle
 * nobody has built a lineup for.
 */
export function shingleLineFor(
  names: readonly (string | null | undefined)[],
): ShingleLine | null {
  for (const name of names) {
    if (!name || name.trim() === "") continue;
    const line = SHINGLE_LINES.find((candidate) =>
      candidate.matches.some((pattern) => pattern.test(name)),
    );
    if (line) return line;
  }
  return null;
}
